import React, { useState, useEffect } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody } from '@chakra-ui/react';
import Cookies from 'js-cookie';  // Import js-cookie

const data = {
    modules: [
        {
            name: 'targets',
            header: 'Targets Module',
            content: [
                {
                    title: 'Search Filter Update',
                    details: [
                        'Previously, the search filter in the Targets module was limited to filtering by the number of the target. This restricted users\' ability to find targets with just the name or other identifying information.',
                        'Update: The search filter has now been enhanced to allow searching by both Name and Number. Users can now filter targets more effectively, regardless of whether they search by the target\'s name or number. This change provides a more flexible and user-friendly search experience, making it easier for users to locate specific targets within a campaign.'
                    ]
                }
            ]
        },
        {
            name: 'cdr',
            header: 'Call Detail Records (CDR) Module',
            content: [
                {
                    title: 'CDR Reporting Enhancements',
                    details: [
                        'Target Name Column: In the past, CDR listings only displayed basic details about the calls without identifying which specific target in the campaign the call was directed to.',
                        'Update: A new column has been added to the CDR listing that shows the Target Name. This makes it easier for users to identify which target in the campaign received the call. This enhancement adds clarity to the data, especially in campaigns with multiple targets, and helps users quickly locate the relevant call information.'
                    ]
                },
                {
                    title: 'CDR Campaign Filter Improvement',
                    details: [
                        'Old System: The CDR Campaign filter was previously a basic input search field. While functional, it was not as efficient when dealing with a large number of campaigns.',
                        'Update: The CDR Campaign Filter has been transformed from a simple input field into a searchable dropdown. This allows users to easily search and select from a list of available campaigns, making it much more efficient and user-friendly when navigating through multiple campaigns. This change enhances the filtering process by allowing for quicker, more accurate selections of campaigns, improving overall usability.'
                    ]
                },
                {
                    title: 'CDR Target Filter Improvement',
                    details: [
                        'Old System: The CDR Target filter was previously a basic input search field. While functional, it was not as efficient when dealing with a large number of targets.',
                        'Update: The CDR Target Filter has been transformed from a simple input field into a searchable dropdown. This allows users to easily search and select from a list of available targets, making it much more efficient and user-friendly when navigating through multiple targets. This change enhances the filtering process by allowing for quicker, more accurate selections of targets, improving overall usability. In addition to that, you now select a campaign and then see its specific targets in the dropdown, filtering out the targets to make it easier for the user.'
                    ]
                }
            ]
        },
        {
            name: 'campaigns',
            header: 'Campaigns Module',
            content: [
                {
                    title: 'Campaign List Enhancements',
                    details: [
                        'Previously, the campaign listing displayed only the basic campaign details, making it difficult for users to get an overview of campaign-specific performance metrics directly within the module.',
                        'Update: The campaign list now includes additional real-time metrics, such as the total number of active calls (`today_cc`) and the total calls made for the day (`today_cap`). This allows users to get a quick snapshot of campaign activity without needing to navigate to separate reports. These updates improve efficiency by providing instant visibility into campaign performance within the same interface.'
                    ]
                },
                {
                    title: 'Pagination and Search Optimization',
                    details: [
                        'Old System: Campaign listings were retrieved without optimized pagination, leading to longer loading times when handling large datasets. Additionally, users could only search by campaign name using an exact match.',
                        'Update: The campaign retrieval process now includes optimized pagination with improved performance, ensuring faster load times even when handling a large number of campaigns. The search functionality has also been enhanced to allow partial matches, enabling users to find campaigns more easily based on any portion of the campaign name. This significantly improves usability, especially in organizations managing multiple campaigns.'
                    ]
                }
            ]
        }
    ]
};

const ChangelogModal = ({ isOpen, onClose }) => {
    const [selectedModule, setSelectedModule] = useState('targets');
    const [hasSeenModal, setHasSeenModal] = useState(false);

    // Check if the user has already seen the modal in the last 24 hours
    useEffect(() => {
        const lastSeen = Cookies.get('changelogSeen');
        const now = new Date().getTime();
        if (lastSeen && now - parseInt(lastSeen) < 24 * 60 * 60 * 1000) {
            setHasSeenModal(true);  // If the user has seen it in the last 24 hours, hide the modal
        }
    }, []);

    // Set the cookie when the modal is closed
    const handleClose = () => {
        Cookies.set('changelogSeen', new Date().getTime().toString(), { expires: 1 }); // Set cookie for 24 hours
        onClose();
    };

    // Find the selected module data based on selectedModule
    const selectedModuleData = data.modules.find(module => module.name === selectedModule);

    if (hasSeenModal) {
        return null;  // Don't show the modal if the user has seen it within the last 24 hours
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={'xl'}>
            <ModalOverlay />
            <ModalContent className='dark:!bg-navy-800 dark:!text-white !rounded-xl'>
                <ModalHeader>Changelog</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <div className="flex justify-center space-x-4 mb-5">
                        {data.modules.map(module => (
                            <button
                                key={module.name}
                                onClick={() => setSelectedModule(module.name)}
                                className={`text-sm font-semibold ${selectedModule === module.name ? 'text-blue-500' : 'text-navy-700 dark:text-white'}`}
                            >
                                {module.header}
                            </button>
                        ))}
                    </div>

                    {selectedModuleData ? (
                        <div>
                            <h1 className="text-2xl font-semibold text-navy-700 dark:text-white mb-5">{selectedModuleData.header}</h1>
                            {selectedModuleData.content.map((item, index) => (
                                <div key={index}>
                                    <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3">{item.title}</h2>
                                    <ul className="text-sm font-semibold text-navy-700 dark:text-white list-decimal list-inside ml-4">
                                        {item.details.map((detail, detailIndex) => (
                                            <li key={detailIndex} className="text-sm font-semibold text-navy-700 dark:text-white mb-2">{detail}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div>No data available</div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ChangelogModal;
